import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, fmtEUR } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Users, Wallet, TrendingUp, Mail, Building2, CheckCircle2, ShieldCheck, RefreshCw, Trash2, Package,
} from "lucide-react";

const STATUS = {
  active: { label: "Actif", cls: "bg-emerald-100 text-emerald-800" },
  trialing: { label: "Essai en cours", cls: "bg-teal-100 text-teal-800" },
  past_due: { label: "Impayé", cls: "bg-amber-100 text-amber-800" },
  canceled: { label: "Résilié", cls: "bg-rose-100 text-rose-800" },
  unpaid: { label: "Impayé — accès suspendu", cls: "bg-rose-100 text-rose-800" },
  aucun: { label: "Aucun abonnement", cls: "bg-slate-100 text-slate-600" },
};
function statusBadge(s) {
  const m = STATUS[s] || STATUS.aucun;
  return <Badge className={`${m.cls} border-0`}>{m.label}</Badge>;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR"); } catch { return "—"; }
}

function Stat({ icon: Icon, label, value, sub, testid }) {
  return (
    <Card className="p-5 border-slate-200 shadow-none" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-slate-500">
        <Icon className="h-4 w-4" /> {label}
      </div>
      <div className="mt-2 text-3xl font-heading font-bold text-slate-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </Card>
  );
}

export default function AdminSitePage() {
  const { user } = useAuth();
  const [ov, setOv] = useState(null);
  const [clients, setClients] = useState([]);
  const [traffic, setTraffic] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [inv, setInv] = useState(null);
  const [invLoading, setInvLoading] = useState(false);

  const eur = (c) => (c == null ? "—" : (c / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2 }) + " €");

  const loadInventory = async () => {
    setInvLoading(true);
    try {
      const { data } = await api.get("/admin/stripe/inventory");
      setInv(data);
    } catch (e) {
      toast.error("Erreur inventaire Stripe : " + (e.response?.data?.detail || e.message));
    } finally { setInvLoading(false); }
  };

  useEffect(() => {
    if (!user?.is_super_admin) return;
    (async () => {
      try {
        const [o, c, t, m] = await Promise.all([
          api.get("/admin/overview"),
          api.get("/admin/clients"),
          api.get("/admin/traffic?days=30"),
          api.get("/admin/contact-messages"),
        ]);
        setOv(o.data);
        setClients(c.data.clients || []);
        setTraffic(t.data);
        setMessages(m.data.messages || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const markRead = async (id) => {
    await api.patch(`/admin/contact-messages/${id}/read`);
    setMessages((ms) => ms.map((x) => (x.id === id ? { ...x, read: true } : x)));
  };

  const reconcileAll = async () => {
    setSyncingAll(true);
    try {
      const { data } = await api.post("/admin/clients/reconcile-all");
      toast.success(`${data.linked} relié(s) à Stripe · ${data.no_subscription + data.no_customer} sans abonnement Stripe`);
      const [o, c] = await Promise.all([api.get("/admin/overview"), api.get("/admin/clients")]);
      setOv(o.data); setClients(c.data.clients || []);
    } catch {
      toast.error("Échec de la synchronisation globale");
    } finally {
      setSyncingAll(false);
    }
  };

  const delCli = async (id, name) => {
    if (!window.confirm(`Supprimer définitivement le compte « ${name} » ? Son abonnement Stripe éventuel sera annulé. Action irréversible.`)) return;
    setDeleting(id);
    try {
      const { data } = await api.delete(`/admin/clients/${id}`);
      toast.success(data.subscription_canceled ? "Compte supprimé et abonnement Stripe annulé" : "Compte supprimé");
      const [o, c] = await Promise.all([api.get("/admin/overview"), api.get("/admin/clients")]);
      setOv(o.data); setClients(c.data.clients || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    } finally {
      setDeleting(null);
    }
  };

  const reconcile = async (id) => {
    setSyncing(id);
    try {
      const { data } = await api.post(`/admin/clients/${id}/reconcile-stripe`);
      if (data.linked) {
        toast.success(`Compte relié à Stripe${data.plan ? ` (${data.plan})` : ""}`);
        const [o, c] = await Promise.all([api.get("/admin/overview"), api.get("/admin/clients")]);
        setOv(o.data); setClients(c.data.clients || []);
      } else {
        const reasons = {
          no_customer: "Aucun client Stripe pour cet email",
          no_subscription: "Aucun abonnement Stripe trouvé",
          no_email: "Email manquant",
          stripe_error: "Erreur Stripe",
        };
        toast.error(reasons[data.reason] || "Impossible de relier ce compte à Stripe");
      }
    } catch {
      toast.error("Échec de la synchronisation Stripe");
    } finally {
      setSyncing(null);
    }
  };

  if (!user?.is_super_admin) return <Navigate to="/app" replace />;

  return (
    <div className="space-y-6" data-testid="admin-site-page">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-6 w-6 text-[hsl(var(--primary))]" />
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Gestion du site</h1>
          <p className="text-sm text-slate-500">Vue d'ensemble de votre activité Gestimmony (réservé au super-admin).</p>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="admin-tab-overview">Vue d'ensemble</TabsTrigger>
          <TabsTrigger value="clients" data-testid="admin-tab-clients">Clients ({clients.length})</TabsTrigger>
          <TabsTrigger value="traffic" data-testid="admin-tab-traffic">Trafic</TabsTrigger>
          <TabsTrigger value="messages" data-testid="admin-tab-messages">
            Messages {ov?.unread_messages ? `(${ov.unread_messages})` : ""}
          </TabsTrigger>
          <TabsTrigger value="catalog" data-testid="admin-tab-catalog">Catalogue Stripe</TabsTrigger>
        </TabsList>

        {/* Vue d'ensemble */}
        <TabsContent value="overview" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat icon={Users} label="Clients" value={ov?.clients_total ?? "—"}
              sub={`${ov?.clients_active ?? 0} actifs · ${ov?.clients_suspended ?? 0} sans abonnement actif`} testid="admin-stat-clients" />
            <Stat icon={Wallet} label="Revenus mensuels (est.)" value={ov ? fmtEUR(ov.mrr_estimated) : "—"}
              sub="MRR HT estimé des abonnements actifs" testid="admin-stat-mrr" />
            <Stat icon={TrendingUp} label="Revenus annuels (est.)" value={ov ? fmtEUR(ov.arr_estimated) : "—"}
              sub="ARR HT estimé" testid="admin-stat-arr" />
            <Stat icon={Mail} label="Messages non lus" value={ov?.unread_messages ?? "—"}
              sub="Formulaire de contact" testid="admin-stat-unread" />
          </div>
          <Card className="p-5 border-slate-200 shadow-none">
            <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-3">Abonnements actifs par formule</div>
            <div className="grid grid-cols-3 gap-4">
              {["essentiel", "patrimoine", "prestige"].map((t) => (
                <div key={t} className="rounded-lg bg-slate-50 p-4 text-center" data-testid={`admin-tier-${t}`}>
                  <div className="text-2xl font-bold text-slate-900">{ov?.by_tier?.[t] ?? 0}</div>
                  <div className="text-xs text-slate-500 capitalize mt-1">{t}</div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* Clients */}
        <TabsContent value="clients" className="mt-6">
          <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-slate-500 max-w-xl">Le statut affiché provient de Stripe pour les comptes <b>reliés</b>. « Non relié » = statut local non encore confirmé par Stripe — cliquez « Resync » pour récupérer le vrai abonnement.</p>
            <Button variant="outline" size="sm" disabled={syncingAll} onClick={reconcileAll} data-testid="admin-reconcile-all">
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${syncingAll ? "animate-spin" : ""}`} /> Tout resynchroniser
            </Button>
          </div>
          <Card className="border-slate-200 shadow-none overflow-hidden">
            {/* Desktop */}
            <div className="hidden sm:block">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Bailleur</TableHead>
                    <TableHead>Formule</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Biens</TableHead>
                    <TableHead>Inscrit le</TableHead>
                    <TableHead className="text-right">Stripe</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clients.map((c) => (
                    <TableRow key={c.id} data-testid={`admin-client-${c.id}`}>
                      <TableCell>
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs text-slate-500">{c.email}</div>
                      </TableCell>
                      <TableCell className="text-sm capitalize">{c.tier || "—"}</TableCell>
                      <TableCell>{statusBadge(c.subscription_status)}</TableCell>
                      <TableCell className="text-sm">{c.properties}</TableCell>
                      <TableCell className="text-sm">{fmtDate(c.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {c.stripe_linked
                            ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700" data-testid={`admin-client-linked-${c.id}`}><CheckCircle2 className="h-3.5 w-3.5" />Relié</span>
                            : <span className="text-xs text-slate-400">Non relié</span>}
                          <Button variant="outline" size="sm" disabled={syncing === c.id} onClick={() => reconcile(c.id)} data-testid={`admin-client-resync-${c.id}`}>
                            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${syncing === c.id ? "animate-spin" : ""}`} /> Resync
                          </Button>
                          <Button variant="outline" size="sm" disabled={deleting === c.id} onClick={() => delCli(c.id, c.name)} data-testid={`admin-client-delete-${c.id}`}>
                            <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!loading && clients.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-500 py-8">Aucun client inscrit pour le moment.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            {/* Mobile */}
            <div className="sm:hidden divide-y divide-slate-100">
              {clients.map((c) => (
                <div key={c.id} className="p-4" data-testid={`admin-client-card-${c.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{c.name}</div>
                      <div className="text-xs text-slate-500 truncate">{c.email}</div>
                    </div>
                    {statusBadge(c.subscription_status)}
                  </div>
                  <div className="mt-2 text-sm text-slate-600 flex items-center gap-4">
                    <span className="capitalize">{c.tier || "Aucune formule"}</span>
                    <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{c.properties}</span>
                    <span>{fmtDate(c.created_at)}</span>
                  </div>
                  <div className="mt-3 flex items-center gap-3">
                    <Button variant="outline" size="sm" disabled={syncing === c.id} onClick={() => reconcile(c.id)} data-testid={`admin-client-resync-m-${c.id}`}>
                      <RefreshCw className={`h-3.5 w-3.5 mr-1 ${syncing === c.id ? "animate-spin" : ""}`} /> Resync Stripe
                    </Button>
                    {c.stripe_linked
                      ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" />Relié</span>
                      : <span className="text-xs text-slate-400">Non relié</span>}
                    <Button variant="outline" size="sm" disabled={deleting === c.id} onClick={() => delCli(c.id, c.name)} data-testid={`admin-client-delete-m-${c.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                    </Button>
                  </div>
                </div>
              ))}
              {!loading && clients.length === 0 && (
                <div className="p-8 text-center text-sm text-slate-500">Aucun client inscrit pour le moment.</div>
              )}
            </div>
          </Card>
          <p className="mt-3 text-xs text-slate-500">
            Lecture seule. La suspension est automatique : sans abonnement actif, un client passe en mode consultation jusqu'à régularisation du paiement.
          </p>
        </TabsContent>

        {/* Trafic */}
        <TabsContent value="traffic" className="mt-6">
          <Card className="border-slate-200 shadow-none overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Provenance du trafic — 30 derniers jours</div>
              <div className="text-sm text-slate-600">
                {traffic?.totals?.visits ?? 0} visites · {traffic?.totals?.signups ?? 0} inscriptions · {traffic?.totals?.conversion ?? 0}%
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Source</TableHead>
                  <TableHead className="text-right">Visites</TableHead>
                  <TableHead className="text-right">Inscriptions</TableHead>
                  <TableHead className="text-right">Conversion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(traffic?.rows || []).map((r) => (
                  <TableRow key={r.source} data-testid={`admin-traffic-${r.source}`}>
                    <TableCell className="capitalize font-medium">{r.source}</TableCell>
                    <TableCell className="text-right">{r.visits}</TableCell>
                    <TableCell className="text-right">{r.signups}</TableCell>
                    <TableCell className="text-right">{r.conversion}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Messages */}
        <TabsContent value="messages" className="mt-6 space-y-3">
          {messages.length === 0 && !loading && (
            <Card className="p-8 text-center text-sm text-slate-500 border-slate-200 shadow-none">Aucun message reçu.</Card>
          )}
          {messages.map((m) => (
            <Card key={m.id} className={`p-4 border-slate-200 shadow-none ${m.read ? "" : "border-l-4 border-l-[hsl(var(--primary))]"}`} data-testid={`admin-message-${m.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-slate-900">
                    {m.name} <span className="text-slate-400 font-normal">· {m.email}</span>
                  </div>
                  {m.subject && <div className="text-sm font-medium text-slate-700 mt-0.5">{m.subject}</div>}
                  <div className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{m.message}</div>
                  <div className="text-xs text-slate-400 mt-2">{fmtDate(m.created_at)}</div>
                </div>
                {!m.read && (
                  <Button variant="outline" size="sm" onClick={() => markRead(m.id)} data-testid={`admin-message-read-${m.id}`}>
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Lu
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </TabsContent>

        {/* Catalogue Stripe (inventaire lecture seule) */}
        <TabsContent value="catalog" className="mt-6 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-slate-500 max-w-xl">Inventaire <b>en lecture seule</b> de votre catalogue Stripe (clé active). Affiche l'état réel des produits, prix et identifiants (<span className="font-mono">lookup_key</span>) utilisés par le paiement.</p>
            <Button variant="outline" size="sm" disabled={invLoading} onClick={loadInventory} data-testid="admin-stripe-inventory-load">
              <Package className={`h-3.5 w-3.5 mr-1 ${invLoading ? "animate-pulse" : ""}`} /> Charger l'inventaire
            </Button>
          </div>
          {inv && (
            <>
              <div className="text-xs">Mode : <b className={inv.mode === "live" ? "text-emerald-700" : "text-amber-700"}>{inv.mode === "live" ? "PRODUCTION (live)" : "test"}</b></div>
              <Card className="p-4 border-slate-200 shadow-none">
                <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-3">Identifiants du paiement (lookup_key → prix réellement utilisé)</div>
                <div className="space-y-1" data-testid="admin-stripe-lookups">
                  {Object.entries(inv.lookups).map(([lk, v]) => (
                    <div key={lk} className="flex items-center justify-between gap-2 border-b border-slate-100 py-1">
                      <span className="font-mono text-xs text-slate-700">{lk}</span>
                      {v ? (
                        <span className="text-xs text-right">{eur(v.unit_amount)} · {v.type === "recurring" ? `récurrent / ${v.interval}` : <b className="text-rose-600">PAIEMENT UNIQUE ⚠️</b>}<br /><span className="font-mono text-slate-400">{v.price_id}</span></span>
                      ) : <span className="text-xs text-rose-600 font-semibold">AUCUN PRIX ⚠️</span>}
                    </div>
                  ))}
                </div>
              </Card>
              <div className="space-y-3">
                {inv.products.map((p) => (
                  <Card key={p.id} className="p-4 border-slate-200 shadow-none" data-testid={`admin-stripe-product-${p.id}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="font-medium text-slate-900 text-sm">
                        {p.name}
                        {p.managed && <Badge className="ml-2 bg-teal-100 text-teal-800">créé par l'app</Badge>}
                      </div>
                      <div className="text-xs text-slate-400 font-mono">{p.id} · créé {fmtDate(p.created)}</div>
                    </div>
                    <div className="mt-2 space-y-1">
                      {p.prices.map((pr) => (
                        <div key={pr.id} className="flex items-center justify-between gap-2 text-xs border-b border-slate-100 py-1">
                          <span>{eur(pr.unit_amount)} · {pr.type === "recurring" ? `récurrent / ${pr.interval}` : "paiement unique"}</span>
                          <span className="font-mono text-slate-500 text-right">{pr.lookup_key || "—"}<br /><span className="text-slate-400">{pr.id}</span></span>
                        </div>
                      ))}
                      {p.prices.length === 0 && <div className="text-xs text-slate-400">Aucun prix actif</div>}
                    </div>
                  </Card>
                ))}
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
